// pages/master/tabs/DiscountsTab.jsx
import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Row, Col, Card, Form, Button, Table, Spinner, Badge } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import {
  fetchDiscountsByHouseThunk,
  createDiscountThunk,
  deleteDiscountThunk,
  clearDiscountsError,
} from "../../../../store/slices/discountsSlice";

import "../../../../css/master-common.css";

// ✅ Toast confirm helper (no window.confirm)
const confirmToast = (message, { yesText = "Delete", noText = "Cancel", onYes, onNo } = {}) => {
  toast(
    ({ closeToast }) => (
      <div>
        <div style={{ fontWeight: 800, marginBottom: 8 }}>{message}</div>
        <div style={{ display: "flex", gap: 10 }}>
          <button
            type="button"
            className="btn btn-danger btn-sm"
            onClick={() => {
              closeToast?.();
              onYes?.();
            }}
          >
            {yesText}
          </button>
          <button
            type="button"
            className="btn btn-outline-secondary btn-sm"
            onClick={() => {
              closeToast?.();
              onNo?.();
            }}
          >
            {noText}
          </button>
        </div>
      </div>
    ),
    {
      autoClose: false,
      closeOnClick: false,
      closeButton: false,
      draggable: false,
    }
  );
};

export default function DiscountsTab({home_id, activeTab, onRefreshProfile }) {
  const dispatch = useDispatch();
  const { list = [], loading, saving, deleting, error } = useSelector((s) => s.discounts);

  const [q, setQ] = useState("");
  const [form, setForm] = useState({
    dis_type: "",
    dis_amount: "",
    remarks: "",
  });

  const refresh = useCallback(() => {
    if (!home_id) return;
    dispatch(fetchDiscountsByHouseThunk(home_id));
  }, [home_id, dispatch]);

  // ✅ Fetch when tab becomes active (because tabs are kept mounted)
  useEffect(() => {
    if (!home_id) return;
    if (activeTab !== "discounts") return; // must match your tab key
    refresh();
  }, [home_id, activeTab, refresh]);

  // ✅ toast error (no Alert)
  useEffect(() => {
    if (!error) return;
    toast.error(String(error));
    dispatch(clearDiscountsError());
  }, [error, dispatch]);

  const filtered = useMemo(() => {
    const query = String(q || "").toLowerCase().trim();
    if (!query) return list || [];

    return (list || []).filter((d) => {
      const hay = [d.d_id, d.dis_amount, d.dis_type, d.dis_date, d.remarks]
        .map((v) => String(v ?? "").toLowerCase())
        .join(" ");
      return hay.includes(query);
    });
  }, [list, q]);

const submit = async (e) => {
  e.preventDefault();

  if (!home_id) return toast.warning("Home ID missing");
  if (!form.dis_type || !form.dis_amount) {
    return toast.warning("Discount type & amount required");
  }

  const r = await dispatch(
    createDiscountThunk({
      home_id,
      dis_type: form.dis_type,
      dis_amount: form.dis_amount,
      remarks: form.remarks,
    })
  );

  if (createDiscountThunk.fulfilled.match(r)) {
    toast.success("Discount added ✅");

    setForm({
      dis_type: "",
      dis_amount: "",
      remarks: "",
    });

    refresh();
    onRefreshProfile?.();
  } else {
    const msg =
      r?.payload?.message ||
      r?.payload ||
      r?.error?.message ||
      "Create failed";

    toast.error(String(msg));
  }
};

const doDelete = async (d_id) => {
  const r = await dispatch(deleteDiscountThunk({ d_id, home_id }));

  if (deleteDiscountThunk.fulfilled.match(r)) {
    toast.success("Deleted 🗑️");

    refresh();
    onRefreshProfile?.();
  } else {
    const msg =
      r?.payload?.message ||
      r?.payload ||
      r?.error?.message ||
      "Delete failed";

    toast.error(String(msg));
  }
};

const remove = (d_id) => {
  confirmToast("Delete this discount?", {
    onYes: () => doDelete(d_id),
  });
};
  return (
    <div className="hm-form-page discounts-tab">
      {/* ================= TOP: FORM ================= */}
      <Row className="g-3">
        <Col xs={12}>
          <Card className="hm-form-card">
            <Card.Header className="hm-form-header">
              <div className="discounts-head">
                <div className="discounts-head-left">
                  <h5 className="mb-1 hm-form-title">Add Discount</h5>
                  <div className="hm-form-subtitle">Add water or SLRM discount for this house.</div>
                </div>

                <div className="d-flex align-items-center gap-2">
                  {home_id ? (
                    <Badge bg="light" text="dark" className="hm-form-badge">
                      Home ID: <span className="hm-form-badge-val">{home_id}</span>
                    </Badge>
                  ) : null}

                  {/* ✅ Refresh */}
                  <Button
                    type="button"
                    size="sm"
                    variant="outline-secondary"
                    className="master-btn"
                    onClick={refresh}
                    disabled={!home_id || loading}
                    title="Refresh"
                  >
                    {loading ? "Refreshing..." : "Refresh"}
                  </Button>
                </div>
              </div>
            </Card.Header>

            <Card.Body className="hm-form-body">
              <Form onSubmit={submit}>
                <Row className="g-3">
                  <Col md={4}>
                    <Form.Group className="hm-field">
                      <Form.Label className="hm-label">
                        Discount For <span className="hm-req">*</span>
                      </Form.Label>
                      <Form.Select
                        className="hm-control"
                        required
                        value={form.dis_type}
                        onChange={(e) => setForm((p) => ({ ...p, dis_type: e.target.value }))}
                      >
                        <option value="">Select Discount For</option>
                        <option value="water">Water</option>
                        <option value="slrm">SLRM</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>

                  <Col md={4}>
                    <Form.Group className="hm-field">
                      <Form.Label className="hm-label">
                        Amount <span className="hm-req">*</span>
                      </Form.Label>
                      <Form.Control
                        className="hm-control"
                        type="number"
                        required
                        value={form.dis_amount}
                        onChange={(e) => setForm((p) => ({ ...p, dis_amount: e.target.value }))}
                        placeholder="Enter Discount Amount"
                      />
                      <div className="hm-help">Enter discount amount in rupees.</div>
                    </Form.Group>
                  </Col>

                  <Col md={4}>
                    <Form.Group className="hm-field">
                      <Form.Label className="hm-label">Remarks</Form.Label>
                      <Form.Control
                        className="hm-control"
                        value={form.remarks}
                        onChange={(e) => setForm((p) => ({ ...p, remarks: e.target.value }))}
                        placeholder="Enter remarks (optional)"
                      />
                    </Form.Group>
                  </Col>

                  <Col md={9} className="d-flex align-items-end">
                    <div className="hm-help">
                      Fields marked <span className="hm-req">*</span> are required.
                    </div>
                  </Col>

                  <Col md={3} className="d-flex align-items-end justify-content-end">
                    <Button type="submit" className="hm-primary-btn w-100" disabled={saving || !home_id}>
                      {saving ? (
                        <>
                          <Spinner animation="border" size="sm" className="me-2" />
                          Submitting…
                        </>
                      ) : (
                        "Submit"
                      )}
                    </Button>
                  </Col>
                </Row>
              </Form>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* ================= BELOW: TABLE ================= */}
      <Row className="g-3 mt-1">
        <Col xs={12}>
          <Card className="hm-form-card">
            <Card.Header className="hm-form-header">
              <div className="discounts-head">
                <div className="discounts-head-left">
                  <h5 className="mb-1 hm-form-title">Discounts</h5>
                  <div className="hm-form-subtitle">View and delete discounts.</div>
                </div>

                <div className="discounts-head-right">
                  <Badge bg="light" text="dark" className="hm-form-badge">
                    Total: <span className="hm-form-badge-val">{filtered.length}</span>
                  </Badge>

                  <Form.Control
                    className="hm-control discounts-search"
                    size="sm"
                    placeholder="Search type / amount / remarks..."
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                  />
                </div>
              </div>
            </Card.Header>

            <Card.Body className="hm-form-body">
              {loading ? (
                <div className="hm-form-loading">
                  <Spinner animation="border" />
                  <div className="mt-2 text-muted">Loading discounts...</div>
                </div>
              ) : (
                <div className="master-table-card">
                  <div className="master-table-wrap">
                    <Table bordered hover size="sm" responsive className="mb-0 master-eq-table master-sticky master-row-hover">
                      <thead className="table-light">
                        <tr>
                          <th style={{ width: 60 }}>#</th>
                          <th style={{ width: 130 }}>Action</th>
                          <th style={{ width: 140 }}>Amount</th>
                          <th style={{ width: 140 }}>Type</th>
                          <th style={{ width: 220 }}>Date</th>
                          <th style={{ width: 320 }}>Remarks</th>
                        </tr>
                      </thead>

                      <tbody>
                        {filtered.length === 0 ? (
                          <tr>
                            <td colSpan={6} className="text-center text-muted py-4">
                              No discounts
                            </td>
                          </tr>
                        ) : (
                          filtered.map((d, idx) => (
                            <tr key={d.d_id}>
                              <td>{idx + 1}</td>
                              <td>
                                <Button
                                  size="sm"
                                  variant="outline-danger"
                                  className="master-btn"
                                  disabled={String(deleting) === String(d.d_id)}
                                  onClick={() => remove(d.d_id)}
                                >
                                  {String(deleting) === String(d.d_id) ? "Deleting..." : "Delete"}
                                </Button>
                              </td>
                              <td>₹{d.dis_amount}</td>
                              <td style={{ textTransform: "uppercase" }}>{d.dis_type}</td>
                              <td>{d.dis_date ? new Date(d.dis_date).toLocaleString() : "-"}</td>
                              <td>{d.remarks || "-"}</td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </Table>
                  </div>
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </div>
  );
}